import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import messages from './messages'
import { useQueryReportVendor } from '../../models/useQueryReportVendor'

export interface IUseReportVendorPresenterParams {
  /** Route param — the FS/Page owns routing context (ADR-9) */
  vendorId: number
  /** False when another report config's header hook should fetch instead */
  enabled?: boolean
  /**
   * Toasts a non-404 read failure. Every observer of a react-query v3 query
   * runs its own onError, so only one caller of the shared header query
   * (the detail Feature Set) opts in.
   */
  notifyOnError?: boolean
}

/**
 * Hook-only presenter (ADR-2): resolves the vendor for the detail
 * breadcrumb "{Orders by vendor} / {vendor name}" independent of the
 * line-item fetch. Only `name` is consumed.
 */
export const useReportVendorPresenter = ({
  vendorId,
  enabled = true,
  notifyOnError = false,
}: IUseReportVendorPresenterParams) => {
  const intl = useProcurifyIntl()

  // vendorRepository.getVendor lets the raw HttpError escape; its message
  // ("HTTP Error 500") is not user copy, so only a RepoError's API message
  // is shown.
  const apiMessage = (error: unknown) =>
    error instanceof RepoError ? getMessageFromError(error) : undefined

  // The model reports an unknown id through `isNotFound` and passes only
  // other failures here.
  const handleError = (error: unknown) => {
    toast.error({
      title: intl.formatMessage(messages.errorToastTitle),
      children:
        apiMessage(error) ??
        intl.formatMessage(messages.loadErrorFallbackMessage),
    })
  }

  const { vendorName, isLoading, isNotFound } = useQueryReportVendor(
    vendorId,
    enabled,
    { onError: notifyOnError ? handleError : undefined }
  )

  return {
    data: {
      vendorName,
    },
    states: {
      isLoading,
      isNotFound,
    },
  }
}
